import React, { memo, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import StyledModal from '../StyledModal';
import { Form } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { Formik } from 'formik';
import i18next from 'i18next';
import { isEmpty } from 'lodash';
import moment from 'moment';
import 'moment/locale/de';
import DatePicker from 'react-datepicker'
import styled from 'styled-components';
import { format, parse } from 'date-fns';
import { useExtendedAuth } from '../../util/auth0';
const ErrorDiv = styled.div`
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #dc3545;
`;

type Props = {
    show: boolean,
    onPositive: (date: string, isGlobalTemplate: boolean, templateName: string) => Promise<void>,
    onNegative: () => void,
    initialValues: { date: string, isGlobalTemplate: boolean, templateName: string },
}

interface SaveAndPushModalFormValues {
    date: string;
    isGlobalTemplate: boolean;
    templateName: string;
}

const SaveAndPublishModal = ({
    show,
    onPositive,
    onNegative,
    initialValues,
}: Props) => {
    const { isAdminUser } = useExtendedAuth();
    const maxDate = moment().add(28, 'days');
    const [showGlobalTemplateCheckBox, setShowGlobalTemplateCheckBox] = useState(false);

    useEffect(() => {
        const checkIsAdminUser = async () => {
            const isAdmin = await isAdminUser();
            setShowGlobalTemplateCheckBox(isAdmin);
        }

        checkIsAdminUser();
    }, [isAdminUser]);


    return (
        <Formik<SaveAndPushModalFormValues>
            enableReinitialize={true}
            initialValues={initialValues}
            validateOnMount
            onSubmit={async ({ date, isGlobalTemplate, templateName }, { setSubmitting, setFieldValue }) => {
                onPositive(date, isGlobalTemplate, templateName);
                setSubmitting(false);
                setFieldValue("date", '');
                setFieldValue("isGlobalTemplate", false);
                setFieldValue("templateName", '');
            }}
            validate={({ date, templateName, isGlobalTemplate }) => {
                const errors: { date?: string; templateName?: string } = {};
                if (isEmpty(date)) {
                    errors.date = i18next.t('trainingPlan.validation.date');
                } else {
                    const selectedDate = moment(date, 'DD-MM-YYYY');
                    if (maxDate.diff(selectedDate, 'days') < 0) {
                        errors.date = i18next.t('trainingPlan.validation.greaterDate');
                    }

                    if (selectedDate.diff(maxDate, 'days') > 0) {
                        errors.date = i18next.t('trainingPlan.validation.pastDate');
                    }
                }
                if (showGlobalTemplateCheckBox && isGlobalTemplate && isEmpty(templateName)) {
                    errors.templateName = i18next.t('trainingPlan.validation.templateName');
                }
                return errors;
            }}
        >
            {({ values, errors, isValid, isSubmitting, handleSubmit, setFieldValue }) => (

                <StyledModal size="md" show={show} onHide={() => onNegative()}>
                    <Form onSubmit={handleSubmit} noValidate>
                        <Modal.Header closeButton>
                            <Modal.Title>{i18next.t('trainingPlan.saveAndPublish')}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>
                                {i18next.t('trainingPlan.saveAndPublishModal.body')}
                            </p>
                            <Form.Group controlId="date" bsPrefix="md-form form-group w-50">
                                <Form.Label>{i18next.t('trainingPlan.saveAndPublishModal.label')}</Form.Label>

                                <DatePicker
                                    selected={values.date ? parse(values.date, 'dd-MM-yyyy', new Date()) : null}
                                    onChange={(date: Date | null) => {
                                        if (date) {
                                            const formatted = format(date, 'dd-MM-yyyy');
                                            setFieldValue('date', formatted);
                                        } else {
                                            setFieldValue('date', '');
                                        }
                                    }}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText='DD-MM-YYYY'
                                    className="form-control"
                                    minDate={moment().toDate()}
                                    maxDate={maxDate.toDate()}
                                />
                                {errors && errors.date && (errors.date.length > 0) ? (
                                    <ErrorDiv>{errors.date}</ErrorDiv>
                                ) : null}
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" style={{ marginRight: 'auto' }} onClick={() => onNegative()}>
                                {i18next.t('generic.cancel')}
                            </Button>
                            <Button type="submit" disabled={!isValid || isSubmitting} variant="danger">
                                {i18next.t('trainingPlan.saveAndPublish')}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </StyledModal>
            )}
        </Formik>
    );
};

export default memo(SaveAndPublishModal);
